import {
    ForwardedRef,
    TextareaHTMLAttributes,
    forwardRef,
    useCallback,
    useLayoutEffect,
    useMemo,
    useState,
} from 'react'
import { twMerge as twm } from 'tailwind-merge'

import { Icon } from '../../Icon'

type Props = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> & {
    size?: 'small' | 'medium'
    hasError?: boolean
    isAutoResizing?: boolean
    maxHeight?: number
    onClear?: () => void
    clearButtonClassName?: string
}

const TextArea = forwardRef(
    (
        {
            size = 'small',
            value,
            onChange,
            hasError = false,
            isAutoResizing = false,
            disabled = false,
            className,
            onFocus,
            onBlur,
            onClear,
            maxHeight = 360, // default max height to around 14-15 rows
            clearButtonClassName,
            ...props
        }: Props,
        forwardedRef: ForwardedRef<HTMLTextAreaElement>
    ) => {
        const [isFocused, setIsFocused] = useState(false)

        // We want the caller to be able to specify a ref, but we also need an imperative handle on
        // it, so we use this ID to query it uniquely from the DOM.
        const textAreaID = useMemo(() => Math.random(), [])
        useLayoutEffect(() => {
            if (!isAutoResizing) {
                return
            }

            const element = document.querySelector(`[data-ps-text-area-id="${textAreaID}"]`)
            if (!element || !(element instanceof HTMLTextAreaElement)) {
                return
            }

            const resizeTextArea = () => {
                if (element === null) {
                    return
                }
                element.style.height = 'auto'

                if (maxHeight && element.scrollHeight > maxHeight) {
                    element.style.height = `${maxHeight}px`
                }
                else {
                    element.style.height = element.scrollHeight + 'px'
                }
            }
            resizeTextArea()

            const abortController = new AbortController()
            element.addEventListener('input', resizeTextArea, { signal: abortController.signal })
            return () => abortController.abort()
        }, [textAreaID, isAutoResizing, maxHeight])

        const handleFocus = useCallback(
            (event: React.FocusEvent<HTMLTextAreaElement, Element>) => {
                setIsFocused(true)
                onFocus?.(event)
            },
            [onFocus]
        )
        const handleBlur = useCallback(
            (event: React.FocusEvent<HTMLTextAreaElement, Element>) => {
                setIsFocused(false)
                onBlur?.(event)
            },
            [onBlur]
        )

        const isClearButtonVisible = onClear !== undefined && !!value && isFocused

        const handleClear = () => {
            onClear?.()

            const element = document.querySelector(`[data-ps-text-area-id="${textAreaID}"]`)
            if (!element || !(element instanceof HTMLTextAreaElement)) {
                return
            }
            setTimeout(() => {
                element.focus()
            })
        }

        return (
            <div className="relative w-full">
                <textarea
                    ref={forwardedRef}
                    value={value}
                    onChange={onChange}
                    {...props}
                    disabled={disabled}
                    className={twm(
                        'h-full w-full cursor-pointer rounded-lg border bg-white px-[14px] py-[10px] pr-4 outline-0',
                        hasError && !isFocused ? 'outline-danger-400' : 'outline-primary-700',
                        disabled
                            ? 'border-gray-400'
                            : hasError
                              ? 'border-danger-400'
                              : 'border-gray-500',
                        disabled && 'cursor-not-allowed',
                        isFocused
                            ? 'border-primary-700 outline-2'
                            : !disabled
                              ? 'hover:outline-1'
                              : null,
                        !isFocused &&
                            !disabled &&
                            (hasError ? 'hover:border-danger-400' : 'hover:border-primary-700'),
                        isClearButtonVisible && 'pr-9',
                        className
                    )}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    data-ps-text-area-id={textAreaID}
                />

                {isClearButtonVisible && (
                    <button
                        onMouseDown={handleClear}
                        className={twm(
                            'absolute top-2 right-2 z-20 flex size-7 items-center justify-center rounded-full hover:bg-black/4',
                            clearButtonClassName
                        )}
                    >
                        <Icon icon={size === 'small' ? 'close.small' : 'close.medium'} />
                    </button>
                )}
            </div>
        )
    }
)
TextArea.displayName = 'TextArea'
export { TextArea }
